import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Plus } from 'lucide-react';
import { useNotify } from 'react-admin';
import { HostPersona, logger } from '@musedrops/shared';
import { HostPersonaApi } from '../../../services/hostPersonaApi';

export const PersonasSettings: React.FC = () => {
  const notify = useNotify();
  const [personas, setPersonas] = useState<HostPersona[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state - shared for create and edit
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingPersona, setEditingPersona] = useState<HostPersona | null>(
    null
  );
  const [formData, setFormData] = useState({
    key: '',
    goodFor: '',
    scriptInstructions: '',
    voiceInstructions: '',
  });
  const [formErrors, setFormErrors] = useState({
    key: '',
  });

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPersona, setDeletingPersona] = useState<HostPersona | null>(
    null
  );

  useEffect(() => {
    loadPersonas();
  }, []);

  const loadPersonas = async () => {
    try {
      setLoading(true);
      setError(null);
      const allPersonas = await HostPersonaApi.getAll();
      setPersonas(allPersonas);
    } catch (err) {
      logger.error('Failed to load host personas:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load host personas'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setDialogMode('create');
    setEditingPersona(null);
    setFormData({
      key: '',
      goodFor: '',
      scriptInstructions: '',
      voiceInstructions: '',
    });
    setFormErrors({ key: '' });
    setDialogOpen(true);
  };

  const handleEditClick = (persona: HostPersona) => {
    setDialogMode('edit');
    setEditingPersona(persona);
    setFormData({
      key: persona.key,
      goodFor: persona.goodFor,
      scriptInstructions: persona.scriptInstructions,
      voiceInstructions: persona.voiceInstructions,
    });
    setFormErrors({ key: '' });
    setDialogOpen(true);
  };

  const normalizeKey = (input: string): string => {
    return input
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleKeyChange = (value: string) => {
    const normalized = normalizeKey(value);
    setFormData(prev => ({ ...prev, key: normalized }));
    setFormErrors({ key: '' });
  };

  const validateForm = (): boolean => {
    if (!formData.key.trim()) {
      setFormErrors({ key: 'Key is required' });
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(formData.key)) {
      setFormErrors({
        key: 'Key must contain only lowercase letters, numbers, and hyphens',
      });
      return false;
    }
    return true;
  };

  const handleDialogSave = async () => {
    if (!validateForm()) return;

    if (dialogMode === 'create') {
      try {
        await HostPersonaApi.create(formData);
        notify('Host persona created successfully', { type: 'success' });
        setDialogOpen(false);
        loadPersonas();
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Failed to create host persona';
        if (errorMsg.includes('duplicate') || errorMsg.includes('unique')) {
          setFormErrors({ key: 'A persona with this key already exists' });
        } else {
          notify(errorMsg, { type: 'error' });
        }
      }
    } else {
      if (!editingPersona) return;

      try {
        await HostPersonaApi.update(editingPersona.id, formData);
        notify('Host persona updated successfully', { type: 'success' });
        setDialogOpen(false);
        setEditingPersona(null);
        loadPersonas();
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Failed to update host persona';
        if (errorMsg.includes('duplicate') || errorMsg.includes('unique')) {
          setFormErrors({ key: 'A persona with this key already exists' });
        } else {
          notify(errorMsg, { type: 'error' });
        }
      }
    }
  };

  const handleDeleteClick = (persona: HostPersona) => {
    setDeletingPersona(persona);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPersona) return;

    try {
      await HostPersonaApi.delete(deletingPersona.id);
      notify('Host persona deleted successfully', { type: 'success' });
      setDeleteDialogOpen(false);
      setDeletingPersona(null);
      loadPersonas();
    } catch (err) {
      notify(
        err instanceof Error ? err.message : 'Failed to delete host persona',
        {
          type: 'error',
        }
      );
    }
  };

  const isFormValid =
    formData.key.trim() &&
    formData.goodFor.trim() &&
    formData.scriptInstructions.trim() &&
    formData.voiceInstructions.trim() &&
    !formErrors.key;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '200px' }}>Key</TableCell>
              <TableCell>Good For</TableCell>
              <TableCell align="center" sx={{ width: '100px' }}>
                <IconButton
                  onClick={handleCreateClick}
                  color="primary"
                  size="small"
                >
                  <Plus size={18} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {personas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No host personas yet. Click + to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              personas.map(persona => (
                <TableRow
                  key={persona.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleEditClick(persona)}
                >
                  <TableCell>
                    <code>{persona.key}</code>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{persona.goodFor}</Typography>
                  </TableCell>
                  <TableCell align="center" onClick={e => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(persona)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(persona)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'edit' ? 'Edit Host Persona' : 'Create Host Persona'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              autoFocus
              label="Key"
              fullWidth
              value={formData.key}
              onChange={e => handleKeyChange(e.target.value)}
              error={Boolean(formErrors.key)}
              helperText={
                formErrors.key ||
                'Lowercase letters, numbers, hyphens only (e.g., warm-explainer)'
              }
              required
              disabled={dialogMode === 'edit'} // Keys shouldn't be changed after creation
            />

            <TextField
              label="Good For"
              fullWidth
              value={formData.goodFor}
              onChange={e =>
                setFormData(prev => ({ ...prev, goodFor: e.target.value }))
              }
              helperText="One sentence describing when this persona works well"
              required
            />

            <TextField
              label="Script Instructions"
              fullWidth
              multiline
              rows={8}
              value={formData.scriptInstructions}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  scriptInstructions: e.target.value,
                }))
              }
              helperText="Detailed instructions for writing style, tone, and language patterns"
              required
            />

            <TextField
              label="Voice Instructions"
              fullWidth
              value={formData.voiceInstructions}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  voiceInstructions: e.target.value,
                }))
              }
              helperText="Concise instructions for TTS voice characteristics (e.g., warm | mid tempo | encouraging)"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDialogSave}
            variant="contained"
            disabled={!isFormValid}
          >
            {dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Host Persona</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the persona{' '}
            <code>{deletingPersona?.key}</code>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone. Shows using this persona will need to
            be reassigned.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
